import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTracking } from "../hooks/useTracking";
import { ActionCategory } from "../services/logging/logService";
import axios from "axios";
import { savePdf } from "./print/backorder/Backorder";
import { downloadCSV } from "./print/backorder/BackorderCSV";
import moment from "moment";
import CloseIcon from "../assets/images/close_icon.png";
import { FaInfoCircle } from "react-icons/fa";
import { useSelector } from "react-redux";
import BeatLoader from "react-spinners/BeatLoader";
import classNames from "classnames";
import Select from "../components/Select";
import { Field, Form, Formik } from "formik";
import { getAuthUrl } from "../config/auth";
import { handleLogout } from "../config/axios";
import BackorderFiltersModal from "../components/BackorderFiltersModal";
import decryptData from "../decrypt";
import {
  fetchUserInfo,
  fetchClientsByAgentCodes,
  getAllAgentCodes,
  processAgentCodeValue,
} from "../services/userService";

interface Agent {
  db2_user_id: string;
  first_name: string;
  last_name: string;
}

interface SelectOption {
  value: string;
  label: string;
}

/**
 * Funzione per registrare direttamente le azioni di ricerca senza usare il sistema di debounce
 */
const logSearchDirectly = (
  action: string,
  description: string,
  metadata: any
) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.warn("No auth token found, cannot log search");
      return;
    }

    // Crea un payload di log simile a quello usato dal logService, ma con ID univoco
    const logMessage = {
      action: `search:${action}`,
      description,
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      sessionId: `direct_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      metadata: {
        ...metadata,
        directLog: true,
        timestamp: new Date().toISOString(),
      },
    };

    // Payload completo per l'API
    const payload = {
      level: "info",
      serviceName: "api",
      message: logMessage,
    };

    // Chiamata diretta all'API senza debounce
    axios
      .post("/nodeapi/writeLog", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        console.log(`Log di ricerca inviato direttamente: ${action}`);
      })
      .catch((error) => {
        console.error("Errore nell'invio diretto del log:", error);
      });
  } catch (error) {
    console.error("Errore nella preparazione del log diretto:", error);
  }
};

export default function Backorder() {
  // Initialize tracking hook
  const tracking = useTracking();
  
  // Load saved filters from localStorage initially
  const getSavedFilters = () => {
    const saved = localStorage.getItem("backorder-filters");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing saved filters:", e);
        return null;
      }
    }
    return null;
  };
  
  const savedFilters = getSavedFilters();
  
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const formikRef: any = useRef();
  const [testata, setTestata] = useState<any>({});
  const [clienteFilters, setClienteFilters] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<SelectOption | null>(savedFilters?.selectedAgent || null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<string | null>(savedFilters?.selectedCliente || null);
  const [searchCliente, setSearchCliente] = useState<string>(savedFilters?.searchCliente || "");
  const [codiceArticolo, setCodiceArticolo] = useState<string>(savedFilters?.codiceArticolo || "");
  const [subordinateAgents, setSubordinateAgents] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fromDelivery, setFromDelivery] = useState<string | null>(savedFilters?.fromDelivery || null);
  const [toDelivery, setToDelivery] = useState<string | null>(savedFilters?.toDelivery || null);
  const [ritiroCliente, setRitiroCliente] = useState<string>(savedFilters?.ritiroCliente || "Tutti");
  const [tipoConsegna, setTipoConsegna] = useState<string>(savedFilters?.tipoConsegna || "Tutti");
  const [theme, setTheme] = useState("light");
  const [fromOrderDate, setFromOrderDate] = useState<string | null>(savedFilters?.fromOrderDate || null);
  const [toOrderDate, setToOrderDate] = useState<string | null>(savedFilters?.toOrderDate || null);
  const [orderBy, setOrderBy] = useState<
    { column: string; mode: "ASC" | "DESC" }[]
  >(savedFilters?.orderBy || []);
  const itemsPerPage = 100;
  const navigate = useNavigate();
  const visibleColumns = ["ragione_sociale", "data_ordine", "data_consegna"];
  const currentTheme = useSelector((state: any) => state.menu.theme);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showOrderBy, setShowOrderBy] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [quantita, setQuantita] = useState<string>(savedFilters?.quantita || "Tutti");
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Use refs to track requests and prevent duplicates
  const currentRequestId = useRef<number>(0);
  const dataFetchTriggered = useRef<boolean>(false);

  // Update theme when it changes
  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  // Save filters to localStorage when they change
  const saveFiltersToLocalStorage = () => {
    const filters = {
      selectedAgent,
      selectedCliente,
      searchCliente,
      codiceArticolo,
      fromDelivery,
      toDelivery,
      ritiroCliente,
      tipoConsegna,
      fromOrderDate,
      toOrderDate,
      orderBy,
      quantita,
    };
    localStorage.setItem("backorder-filters", JSON.stringify(filters));
  };

  // Handle saved filters after clienteFilters are loaded
  useEffect(() => {
    if (clienteFilters.length > 0 && selectedCliente) {
      console.log("📊 Cliente filters loaded, updating search cliente");
      const cliente = clienteFilters.find(
        (c) => c.codice_cliente === selectedCliente
      );
      if (cliente) {
        setSearchCliente(cliente.ragione_sociale);
      }
    }
  }, [clienteFilters, selectedCliente]);

  // Save filters on change
  useEffect(() => {
    saveFiltersToLocalStorage();
  }, [
    selectedAgent,
    selectedCliente,
    searchCliente,
    fromDelivery,
    toDelivery,
    ritiroCliente,
    tipoConsegna,
    fromOrderDate,
    toOrderDate,
    orderBy,
    quantita,
  ]);

  // Fetch agents from keycloak
  const fetchAgents = async (): Promise<string[]> => {
    try {
      const {
        user,
        isSupervisor,
        agentCodes,
        agents: fetchedAgents,
      } = await fetchUserInfo();

      if (!user) {
        throw new Error("Errore nel recupero degli agenti");
      }

      // Set user role
      setUserRole(isSupervisor ? "supervisor" : "agent");

      // Format agents for component state - ensure agent codes are correctly formatted
      const formattedAgents = fetchedAgents.map((agent) => {
        // Make sure we're handling db2_user_id correctly for the dropdown
        // We'll use the all_codes array directly instead of comma-separated string
        return {
          // Store raw array for proper processing later
          db2_user_id: agent.all_codes
            ? JSON.stringify(agent.all_codes)
            : agent.db2_user_id,
          first_name: agent.first_name,
          last_name: agent.last_name,
        };
      });

      // For regular agents with no group members, create an agent entry
      if (
        !isSupervisor &&
        formattedAgents.length === 0 &&
        agentCodes.length > 0
      ) {
        const currentAgent = {
          // Store as JSON array for consistency
          db2_user_id: JSON.stringify(agentCodes),
          first_name: user.firstName || "",
          last_name: user.lastName || "",
        };

        formattedAgents.push(currentAgent);
      }

      console.log("Formatted agents for dropdown:", formattedAgents);

      setAgents(formattedAgents);
      // Make sure subordinate agents are individual codes, not comma-separated
      setSubordinateAgents(agentCodes);

      return agentCodes;
    } catch (error) {
      console.error("Errore nel recupero agenti da Keycloak:", error);
      return [];
    }
  };

  // Fetch customer filters
  const fetchFilters = async () => {
    try {
      // Log initial state for debugging
      console.log("Agents array:", agents);

      // Get all agent codes using the centralized service
      const agentCodes = await getAllAgentCodes();
      console.log("All unique agent codes for filters:", agentCodes);

      // Use the service to fetch clients
      // The service will handle getting and processing all agent codes
      const clients = await fetchClientsByAgentCodes(agentCodes);

      // Map the clients to the expected format
      const mappedFilters = Array.isArray(clients)
        ? clients.map((item: any) => ({
            codice_cliente: item.CODICE_CLIENTE,
            ragione_sociale: item.RAGIONE_SOCIALE,
          }))
        : [];

      setClienteFilters(mappedFilters);
    } catch (error) {
      console.error("Error fetching filters:", error);

      // Handle auth errors
      if (
        error.status === 401 ||
        error.status === 403 ||
        error.status === 500
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.setItem("redirectAfterLogin", window.location.pathname);
        window.location.href = getAuthUrl(window.location.pathname);
        return;
      }
    }
  };

  useEffect(() => {
    if (agents.length > 0) {
      fetchFilters();
    }
  }, [agents]);

  // Main data fetching function
  const fetchData = async (initialRequestBody = null): Promise<void> => {
    // Prevent duplicate calls, but log more info for debugging
    if (dataFetchTriggered.current) {
      console.log(
        "📊 Data fetch already in progress, skipping. Called with:",
        initialRequestBody ? "initial body" : "current state"
      );
      return;
    }

    console.log(
      "📊 Starting fetchData",
      initialRequestBody ? "with initial body" : "with current state"
    );
    dataFetchTriggered.current = true;

    try {
      setLoading(true);

      // Get agent list from initial request or current state
      const agentiFromRequest = initialRequestBody?.agenti || [];
      let agentsToUse = [...agentiFromRequest];

      // If not provided in request, use centralized agent code handling
      if (agentsToUse.length === 0) {
        if (selectedAgent?.value) {
          agentsToUse = processAgentCodeValue(selectedAgent.value);
        } else {
          agentsToUse = await getAllAgentCodes();
        }
      }

      // Properly split any comma-separated agent codes into separate array elements
      agentsToUse = agentsToUse.flatMap((code) => {
        if (typeof code === "string" && code.includes(",")) {
          // Split by commas and return as separate array elements
          return code
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
        }
        return code ? [code] : [];
      });

      // Ensure we don't have any nested arrays or comma-strings remaining
      agentsToUse = agentsToUse.map((code) => {
        // If the code is an array, stringify it to handle any nested structure
        if (Array.isArray(code)) {
          return code.toString();
        }
        return code;
      });

      console.log("📊 Using agents:", agentsToUse);

      // Skip API calls if no agents available
      if (agentsToUse.length === 0) {
        console.warn("No agents available, skipping API calls");
        setData([]);
        setTestata({
          quantita_ordinata: 0,
          importo_ordinato: 0,
          quantita_assegnata: 0,
          importo_assegnato: 0,
        });
        setTotalPages(0);
        setLoading(false);
        dataFetchTriggered.current = false;
        return;
      }

      // Use a request ID to track the latest request
      const requestId = Date.now();
      currentRequestId.current = requestId;

      // Log the final array of agent codes being used
      console.log(
        "📊 Final processed agent codes for API:",
        JSON.stringify(agentsToUse)
      );

      // Prepare request bodies for both endpoints
      const baseRequestBody = {
        pageSize: itemsPerPage,
        // Use agent codes directly - they're already processed
        agenti: agentsToUse,
        ritiro_cliente: ritiroCliente,
        tipologia_consegna: tipoConsegna,
        ...(fromOrderDate &&
          toOrderDate && {
            data_ordine: [
              fromOrderDate.replace(/-/g, ""),
              toOrderDate.replace(/-/g, ""),
            ],
          }),
        ...(fromDelivery &&
          toDelivery && {
            data_di_consegna: [
              fromDelivery.replace(/-/g, ""),
              toDelivery.replace(/-/g, ""),
            ],
          }),
        ...(selectedCliente && { codice_cliente: selectedCliente }),
        ...(codiceArticolo && { codice_articolo: codiceArticolo }),
        quantita: quantita,
        ...(orderBy.length > 0 && {
          campo_ordinamento: orderBy[0].column,
          tipo_ordinamento: orderBy[0].mode,
        }),
      };

      const tableRequestBody = initialRequestBody || {
        ...baseRequestBody,
        page: page + 1,
      };

      const headerRequestBody = {
        ...baseRequestBody,
        page: 1,
      };

      console.log("📊 Making API calls with request ID:", requestId);
      console.log("📊 Table request body:", JSON.stringify(tableRequestBody));
      console.log("📊 Header request body:", JSON.stringify(headerRequestBody));

      // Additional check on agent codes format
      console.log(
        "📊 Agent codes format check - Table:",
        JSON.stringify(tableRequestBody.agenti)
      );
      console.log(
        "📊 Agent codes format check - Header:",
        JSON.stringify(headerRequestBody.agenti)
      );

      // Make API calls in parallel
      const [tableResponse, headerResponse] = await Promise.all([
        fetch("/nodeapi/backorders", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(tableRequestBody),
        }),
        fetch("/nodeapi/backorderHeader", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(headerRequestBody),
        }),
      ]);

      if (tableResponse.status !== 200 && tableResponse.status !== 201) {
        handleLogout(
          tableResponse,
          "/nodeapi/backorders",
          JSON.stringify(tableRequestBody)
        );
        return;
      }

      if (headerResponse.status !== 200 && headerResponse.status !== 201) {
        handleLogout(
          headerResponse,
          "/nodeapi/backorderHeader",
          JSON.stringify(headerRequestBody)
        );
        return;
      }

      const [tableData, headerData] = await Promise.all([
        tableResponse.json(),
        headerResponse.json(),
      ]);

      const decrypted = decryptData(
        tableData.encryptedData,
        tableData.encryptedKey,
        tableData.iv
      );
      const decryptedJson = JSON.parse(decrypted);

      console.log("📊 Data fetched successfully:", decryptedJson)

      const decryptedHead = decryptData(
        headerData.encryptedData,
        headerData.encryptedKey,
        headerData.iv
      );
      const decryptedJsonHead = JSON.parse(decryptedHead);

      console.log(
        "📊 Data processed. Records:",
        decryptedJson?.data?.length || 0
      );

      console.log("HEADERRR: ", decryptedJsonHead);

      setData(decryptedJson.data || []);
      setTotalPages(decryptedJson.pagination?.totalPages || 0);

      if (decryptedJsonHead.data && decryptedJsonHead.data.length > 0) {
        const header = decryptedJsonHead.data[0];
        setTestata({
          quantita_ordinata: header?.VALORE_QUANTITA_NON_EVASA || 0,
          importo_ordinato: header?.VALORE_ORDINI_NON_EVASI || 0,
          quantita_assegnata: header?.QUANTITA_ASSEGNATA_NON_EVASA || 0,
          importo_assegnato: header?.VALORE_ORDINI_ASSEGNATO || 0,
        });
      } else {
        setTestata({
          quantita_ordinata: 0,
          importo_ordinato: 0,
          quantita_assegnata: 0,
          importo_assegnato: 0,
        });
      }

      // Make sure filtersApplied is set to true after successful data fetch
      setFiltersApplied(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalPages(0);
      setTestata({
        quantita_ordinata: 0,
        importo_ordinato: 0,
        quantita_assegnata: 0,
        importo_assegnato: 0,
      });
    } finally {
      setLoading(false);
      console.log("📊 Data fetch complete");
      // Reset the fetch trigger
      dataFetchTriggered.current = false;
    }
  };

  // Initialize the component
  const initializeData = async (): Promise<void> => {
    // Skip if we've already initialized or a fetch is in progress
    if (initialFetchDone || dataFetchTriggered.current) return;

    console.log("📊 Starting initialization process");

    try {
      setLoading(true);
      dataFetchTriggered.current = true;

      // 1. First load agents
      const agentIds = await fetchAgents();
      console.log("📊 Agents loaded:", agentIds.length);

      // Check if we have significant filters that should trigger a search
      const hasSignificantFilters = !!(selectedCliente ||
                               codiceArticolo ||
                               fromDelivery ||
                               toDelivery || 
                               fromOrderDate || 
                               toOrderDate ||
                               (ritiroCliente && ritiroCliente !== "Tutti") ||
                               (tipoConsegna && tipoConsegna !== "Tutti") ||
                               (quantita && quantita !== "Tutti") ||
                               orderBy?.length > 0 ||
                               selectedAgent);
      
      console.log("📊 Has significant filters:", hasSignificantFilters);

      // 2. Only proceed with data fetch if we have agents
      if (agentIds.length > 0) {
        console.log("📊 Building request with agents:", agentIds);

        // Properly process agent codes to ensure they're individual elements
        let processedAgentIds;
        
        // If a specific agent was selected in saved filters, use it
        if (selectedAgent?.value) {
          processedAgentIds = processAgentCodeValue(selectedAgent.value);
        } else {
          // Otherwise use all agent codes (includes all subordinates)
          processedAgentIds = await getAllAgentCodes();
        }

        console.log(
          "📊 Processed agent codes for initialization:",
          processedAgentIds
        );

        // Build request body with saved filters if they exist
        const requestBody = {
          page: 1,
          pageSize: itemsPerPage,
          agenti: processedAgentIds,
          ritiro_cliente: ritiroCliente,
          tipologia_consegna: tipoConsegna,
          quantita: quantita,
          
          // Include saved filter values if they exist
          ...(fromOrderDate && toOrderDate && {
            data_ordine: [
              fromOrderDate.replace(/-/g, ""),
              toOrderDate.replace(/-/g, ""),
            ],
          }),
          ...(fromDelivery && toDelivery && {
            data_di_consegna: [
              fromDelivery.replace(/-/g, ""),
              toDelivery.replace(/-/g, ""),
            ],
          }),
          ...(selectedCliente && { codice_cliente: selectedCliente }),
        ...(codiceArticolo && { codice_articolo: codiceArticolo }),
          ...(orderBy.length > 0 && {
            campo_ordinamento: orderBy[0].column,
            tipo_ordinamento: orderBy[0].mode,
          }),
        };

        // Critical: Reset the flag before calling fetchData
        dataFetchTriggered.current = false;

        // Make the data fetch call with our request body
        console.log("📊 Calling fetchData from initializeData with filters applied:", hasSignificantFilters);
        await fetchData(requestBody);
        
        // If filters were applied, mark them as applied
        if (hasSignificantFilters) {
          setFiltersApplied(true);
        }
      } else {
        console.warn("No agents available after initialization");
        setData([]);
        setTotalPages(0);
        setTestata({
          quantita_ordinata: 0,
          importo_ordinato: 0,
          quantita_assegnata: 0,
          importo_assegnato: 0,
        });
      }
    } catch (error) {
      console.error("Error in initialization:", error);
      setData([]);
      setTotalPages(0);
    } finally {
      console.log("📊 Initialization complete");
      setInitialFetchDone(true);
      setLoading(false);
      // Reset the fetch trigger for future use
      dataFetchTriggered.current = false;
    }
  };

  // Apply filters handler
  const applyFilters = async () => {
    if (dataFetchTriggered.current) return;

    setPage(0);
    setFiltersApplied(true);
    
    // Build the request body with proper agent codes
    let agentCodesToUse = [];
    if (selectedAgent?.value) {
      agentCodesToUse = processAgentCodeValue(selectedAgent.value);
    } else {
      agentCodesToUse = await getAllAgentCodes();
    }
    
    const requestBody = {
      page: 1,
      pageSize: itemsPerPage,
      agenti: agentCodesToUse,
      ritiro_cliente: ritiroCliente,
      tipologia_consegna: tipoConsegna,
      ...(fromOrderDate && toOrderDate && {
        data_ordine: [
          fromOrderDate.replace(/-/g, ""),
          toOrderDate.replace(/-/g, ""),
        ],
      }),
      ...(fromDelivery && toDelivery && {
        data_di_consegna: [
          fromDelivery.replace(/-/g, ""),
          toDelivery.replace(/-/g, ""),
        ],
      }),
      ...(selectedCliente && { codice_cliente: selectedCliente }),
        ...(codiceArticolo && { codice_articolo: codiceArticolo }),
      quantita: quantita,
      ...(orderBy.length > 0 && {
        campo_ordinamento: orderBy[0].column,
        tipo_ordinamento: orderBy[0].mode,
      }),
    };

    fetchData(requestBody);
  };

  // Search handler for button click
  // Search handler for button click
  const handleSearch = async () => {
    console.log("📊 Search button clicked");

    if (dataFetchTriggered.current) {
      console.log("📊 Data fetch in progress, skipping search");
      return;
    }

    // 1. Track search action with standard tracking system
    tracking.trackSearch(
      "Ricerca Backorder " + new Date().getTime(),
      undefined,
      {
        filters: {
          selectedAgent,
          selectedCliente,
          fromOrderDate,
          toOrderDate,
          fromDelivery,
          toDelivery,
          ritiroCliente,
          tipoConsegna,
          quantita,
          orderBy,
        },
        userRole,
        timestamp: new Date().toISOString(),
      }
    );

    // 2. Track search action with direct method
    logSearchDirectly("backorder_search", "Ricerca Backorder (Direct Log)", {
      filters: {
        selectedAgent,
        selectedCliente,
        fromOrderDate,
        toOrderDate,
        fromDelivery,
        toDelivery,
        ritiroCliente,
        tipoConsegna,
        quantita,
        orderBy,
      },
      userRole,
      timestamp: new Date().toISOString(),
    });

    // Forziamo la pagina a 1
    setPage(0);
    setFiltersApplied(true);

    // Process agent codes properly if an agent is selected
    let agentCodesToUse = [];
    if (selectedAgent?.value) {
      // Use our utility function to process the agent codes
      agentCodesToUse = processAgentCodeValue(selectedAgent.value);
      console.log("📊 Processed agent codes for search:", agentCodesToUse);
    } else {
      // Use all agent codes when no specific agent is selected
      // This should include all subordinate agents plus the user's own codes
      agentCodesToUse = await getAllAgentCodes();
      console.log("📊 Using all agent codes for search:", agentCodesToUse);
    }

    const currentRequestBody = {
      page: 1, // vado in pagina 1
      pageSize: itemsPerPage,
      agenti: agentCodesToUse,
      ritiro_cliente: ritiroCliente,
      tipologia_consegna: tipoConsegna,

      // 1) Se fromOrderDate e toOrderDate sono valorizzati, spedisco data_ordine
      ...(fromOrderDate &&
        toOrderDate && {
          data_ordine: [
            fromOrderDate.replace(/-/g, ""),
            toOrderDate.replace(/-/g, ""),
          ],
        }),

      // 2) Se fromDelivery e toDelivery sono valorizzati, spedisco data_di_consegna
      ...(fromDelivery &&
        toDelivery && {
          data_di_consegna: [
            fromDelivery.replace(/-/g, ""),
            toDelivery.replace(/-/g, ""),
          ],
        }),

      // 3) Se esiste il cliente selezionato
      ...(selectedCliente && { codice_cliente: selectedCliente }),
      ...(codiceArticolo && { codice_articolo: codiceArticolo }),

      quantita: quantita,

      // 4) Se c’è un ordinamento
      ...(orderBy.length > 0 && {
        campo_ordinamento: orderBy[0].column,
        tipo_ordinamento: orderBy[0].mode,
      }),
    };

    // Passo questo body alla fetch
    fetchData(currentRequestBody);
  };

  // Component initialization
  useEffect(() => {
    console.log("📊 Component mounted, initializing data");
    initializeData();
  }, []);

  // Handle page changes
  // Handle page changes
  useEffect(() => {
    if (initialFetchDone) {
      // Remove the "page > 0" condition
      console.log("📊 Page changed to", page);
      fetchData();
    }
  }, [page, initialFetchDone]);

  // HANDLERS
  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      // Recuperiamo i filtri attuali dal form
      // Use centralized function to process agent codes
      let agentCodesToUse = [];
      if (selectedAgent?.value) {
        // Process the selected agent's value
        agentCodesToUse = processAgentCodeValue(selectedAgent.value);
        // Properly split any comma-separated agent codes
        agentCodesToUse = agentCodesToUse.flatMap((code) => {
          if (typeof code === "string" && code.includes(",")) {
            return code
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean);
          }
          return code ? [code] : [];
        });
      } else {
        // Get all agent codes from the service
        agentCodesToUse = await getAllAgentCodes();
      }

      const filters = {
        agenti: agentCodesToUse,
        page: 1,
        pageSize: 10000,
        ritiro_cliente: ritiroCliente,
        tipologia_consegna: tipoConsegna,

        ...(fromOrderDate &&
          toOrderDate && {
            data_ordine: [
              fromOrderDate.replace(/-/g, ""),
              toOrderDate.replace(/-/g, ""),
            ],
          }),
        ...(fromDelivery &&
          toDelivery && {
            data_di_consegna: [
              fromDelivery.replace(/-/g, ""),
              toDelivery.replace(/-/g, ""),
            ],
          }),

        ...(selectedCliente && { codice_cliente: selectedCliente }),
        ...(codiceArticolo && { codice_articolo: codiceArticolo }),
        quantita: quantita,
        ...(orderBy.length > 0 && {
          campo_ordinamento: orderBy[0].column,
          tipo_ordinamento: orderBy[0].mode,
        }),
      };

      // Nuova chiamata API per ottenere tutti i dati
      const response = await fetch("/nodeapi/backorders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(filters),
      });

       if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/backorders", JSON.stringify(filters));
         return;
       }


      const result = await response.json();
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);

      // Verifica se abbiamo dati
      if (!decryptedJson.data || decryptedJson.data.length === 0) {
        console.warn("⚠️ Nessun dato disponibile per il PDF.");
        return;
      }

      // Generiamo il PDF con tutti i dati ricevuti
      await savePdf(decryptedJson.data, testata);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error("Errore durante la generazione del PDF:", error);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleCSVDownload = async () => {
    try {
      // Use centralized function to process agent codes
      let agentCodesToUse = [];
      if (selectedAgent?.value) {
        // Process the selected agent's value
        agentCodesToUse = processAgentCodeValue(selectedAgent.value);
        // Properly split any comma-separated agent codes
        agentCodesToUse = agentCodesToUse.flatMap((code) => {
          if (typeof code === "string" && code.includes(",")) {
            return code
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean);
          }
          return code ? [code] : [];
        });
      } else {
        // Get all agent codes from the service
        agentCodesToUse = await getAllAgentCodes();
      }

      const filters = {
        agenti: agentCodesToUse,
        ritiro_cliente: ritiroCliente,
        tipologia_consegna: tipoConsegna,
        ...(fromOrderDate &&
          toOrderDate && {
            data_ordine: [
              fromOrderDate.replace(/-/g, ""),
              toOrderDate.replace(/-/g, ""),
            ],
          }),
        ...(fromDelivery &&
          toDelivery && {
            data_di_consegna: [
              fromDelivery.replace(/-/g, ""),
              toDelivery.replace(/-/g, ""),
            ],
          }),
        ...(selectedCliente && { codice_cliente: selectedCliente }),
        ...(codiceArticolo && { codice_articolo: codiceArticolo }),
        ...(quantita !== "Tutti" && { quantita }), // Includi quantita solo se impostata diversamente da "Tutti"
        ...(orderBy.length > 0 && {
          campo_ordinamento: orderBy[0].column,
          tipo_ordinamento: orderBy[0].mode,
        }),
      };

      console.log("Payload per il CSV:", JSON.stringify(filters, null, 2));

      await downloadCSV(filters);
    } catch (error) {
      console.error("Errore durante il download del CSV:", error);
    }
  };

  const handleAgentSelect = async (option: SelectOption | null) => {
    console.log("Selected agent option:", option);
    setSelectedAgent(option);
    setSelectedCliente(null);
    setSearchCliente("");

    const agentCodes = option
      ? processAgentCodeValue(option.value)
      : await getAllAgentCodes();

    if (agentCodes.length === 0) return;

    const clients = await fetchClientsByAgentCodes(agentCodes);
    const mappedFilters = Array.isArray(clients)
      ? clients.map((item: any) => ({
          codice_cliente: item.CODICE_CLIENTE,
          ragione_sociale: item.RAGIONE_SOCIALE,
        }))
      : [];
    setClienteFilters(mappedFilters);
  };

  const handleClienteSelect = (option) => {
    if (option) {
      setSelectedCliente(option.value);
      setSearchCliente(option.label);
    } else {
      setSelectedCliente(null);
      setSearchCliente("");
    }
  };

  const handleOrderByChange = (column) => {
    if (orderBy.length > 0 && orderBy[0].column === column) {
      setOrderBy([
        { column, mode: orderBy[0].mode === "ASC" ? "DESC" : "ASC" },
      ]);
    } else {
      setOrderBy([{ column, mode: "ASC" }]);
    }
  };

  const clearOrderBy = () => {
    setOrderBy([]);
    setPage(0);
  };

  const clearFiltri = (e) => {
    e.preventDefault();
    setFromDelivery(null);
    setToDelivery(null);
    setFromOrderDate(null);
    setToOrderDate(null);
    setRitiroCliente("Tutti");
    setTipoConsegna("Tutti");
    setPage(0);
  };

  const clearAllFilters = async (e) => {
    if (e) {
      e.preventDefault();
    }

    // 1. Log clear filters action with standard system
    /*     tracking.logAction(
      ActionCategory.USER_ACTION,
      "clear_filters",
      "User cleared all filters in Backorder",
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    ); */

    // 2. Log clear filters with direct method
    logSearchDirectly(
      "backorder_clear_filters",
      "Reset Filtri Backorder (Direct Log)",
      {
        userRole,
        timestamp: new Date().toISOString(),
      }
    );

    localStorage.removeItem("backorder-filters");

    // Reset del form
    if (formikRef.current) {
      formikRef.current.resetForm({
        values: {
          agente: null,
          cliente: null,
          from: "",
          to: "",
          ritiro_cliente: "Tutti",
          tipo_consegna: "Tutti",
          search: "",
          order_by: [],
          vista: "lista",
        },
      });
    }

    // Reset di tutti gli stati
    setSelectedAgent(null);
    setSelectedCliente(null);
    setSearchCliente("");
    setCodiceArticolo("");
    setFromDelivery(null);
    setToDelivery(null);
    setFromOrderDate(null);
    setToOrderDate(null);
    setRitiroCliente("Tutti");
    setTipoConsegna("Tutti");
    setQuantita("Tutti");
    setOrderBy([]);
    setPage(0);

    // IMPORTANT: After resetting all filters, fetch data with all agent codes
    // to ensure supervisors see all their subordinate agents' data
    console.log("📊 Clearing filters and fetching all agent data");

    try {
      // Get all agent codes using our centralized service
      const allAgentCodes = await getAllAgentCodes();
      console.log("📊 All agent codes after clearing filters:", allAgentCodes);

      // Prepare a request with all codes
      const requestBody = {
        page: 1,
        pageSize: itemsPerPage,
        agenti: allAgentCodes,
        ritiro_cliente: "Tutti",
        tipologia_consegna: "Tutti",
        campo_ordinamento: "data_ordine",
        tipo_ordinamento: "ASC",
        quantita: "Tutti",
      };

      // Fetch data with all agent codes
      dataFetchTriggered.current = false; // Reset trigger to allow fetch
      await fetchData(requestBody);
    } catch (error) {
      console.error("Error fetching data after clearing filters:", error);
    }
  };

  function formatThousands(num) {
    if (num === null || num === undefined) return "0";
    return Math.abs(num).toLocaleString("it-IT", { maximumFractionDigits: 0 });
  }

  const getActiveFiltersCount = () => {
    let count = 0;

    if (selectedAgent) count++;
    if (selectedCliente) count++;
    if (codiceArticolo) count++;
    if (fromDelivery) count++;
    if (toDelivery) count++;
    if (ritiroCliente && ritiroCliente !== "Tutti") count++;
    if (tipoConsegna && tipoConsegna !== "Tutti") count++;
    if (fromOrderDate) count++;
    if (toOrderDate) count++;
    if (orderBy.length > 0) count++;
    if (quantita && quantita !== "Tutti") count++; // <-- MODIFICA QUI ("Tutti" invece di "Tutte")

    return count;
  };

  // Pagination component
  const Pagination = () => {
    const maxVisiblePages = 3;

    const handlePageClick = (newPage) => {
      if (newPage >= 0 && newPage < totalPages) {
        setLoading(true);
        setPage(newPage);
      }
    };

    let startPage = Math.max(0, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="flex justify-center items-center mt-4 mb-8">
        <button
          onClick={() => handlePageClick(0)}
          disabled={page === 0}
          className={`px-3 py-1 mx-1 ${
            page === 0
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          ««
        </button>
        <button
          onClick={() => handlePageClick(page - 1)}
          disabled={page === 0}
          className={`px-3 py-1 mx-1 ${
            page === 0
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          «
        </button>
        {Array.from(
          { length: endPage - startPage + 1 },
          (_, i) => startPage + i
        ).map((pageNumber) => (
          <button
            key={pageNumber}
            onClick={() => handlePageClick(pageNumber)}
            className={`px-3 py-1 mx-1 ${
              page === pageNumber
                ? "bg-blue-600 text-white"
                : theme === "dark"
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            {pageNumber + 1}
          </button>
        ))}
        <button
          onClick={() => handlePageClick(page + 1)}
          disabled={page === totalPages - 1}
          className={`px-3 py-1 mx-1 ${
            page === totalPages - 1
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »
        </button>
        <button
          onClick={() => handlePageClick(totalPages - 1)}
          disabled={page === totalPages - 1}
          className={`px-3 py-1 mx-1 ${
            page === totalPages - 1
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »»
        </button>
      </div>
    );
  };

  return (
    <div className="py-8 pb-24 columns-3xl lg:columns-auto">
      <div className="py-8 columns-2xl lg:columns-auto">
        {loading && page === 0 ? (
          <div className="flex justify-center items-center h-screen">
            <BeatLoader color="#2563eb" />
          </div>
        ) : (
          <Formik
            innerRef={formikRef}
            enableReinitialize={true}
            initialValues={{
              agente: selectedAgent,
              cliente: selectedCliente
                ? { value: selectedCliente, label: searchCliente }
                : null,
              fromDelivery: fromDelivery,
              toDelivery: toDelivery,
              fromOrderDate: fromOrderDate,
              toOrderDate: toOrderDate,
              ritiroCliente: ritiroCliente,
              tipoConsegna: tipoConsegna,
              quantita: quantita,
              orderBy: orderBy,
            }}
            onSubmit={async (values) => {
              setSelectedAgent(values.agente);
              if (values.cliente) {
                setSelectedCliente(values.cliente.value);
                setSearchCliente(values.cliente.label);
              } else {
                setSelectedCliente(null);
                setSearchCliente("");
              }
              setPage(0);
              
              // Build request with proper agent codes
              let agentCodesToUse = [];
              if (values.agente?.value) {
                agentCodesToUse = processAgentCodeValue(values.agente.value);
              } else {
                agentCodesToUse = await getAllAgentCodes();
              }
              
              const requestBody = {
                page: 1,
                pageSize: itemsPerPage,
                agenti: agentCodesToUse,
                ritiro_cliente: ritiroCliente,
                tipologia_consegna: tipoConsegna,
                ...(fromOrderDate && toOrderDate && {
                  data_ordine: [
                    fromOrderDate.replace(/-/g, ""),
                    toOrderDate.replace(/-/g, ""),
                  ],
                }),
                ...(fromDelivery && toDelivery && {
                  data_di_consegna: [
                    fromDelivery.replace(/-/g, ""),
                    toDelivery.replace(/-/g, ""),
                  ],
                }),
                ...(values.cliente && { codice_cliente: values.cliente.value }),
                ...(codiceArticolo && { codice_articolo: codiceArticolo }),
                quantita: quantita,
                ...(orderBy.length > 0 && {
                  campo_ordinamento: orderBy[0].column,
                  tipo_ordinamento: orderBy[0].mode,
                }),
              };
              
              fetchData(requestBody);
            }}
          >
            {({ setFieldValue, values }) => (
              <Form>
                {/* Navbar */}
                <div className="relative">
                  <div className="sm:max-w-4xl px-5 sm:px-12 mx-auto flex-col justify-between mt-[50px] md:mt-50">
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:mb-24">
                      <h2 className="text-4xl text-blue-500 font-bold">
                        Backorder
                      </h2>
                      <button
                        type="button"
                        onClick={() => setShowInfoModal(true)}
                        className="text-blue-400 hover:text-blue-600 mt-1"
                        aria-label="Informazioni Backorder"
                      >
                        <FaInfoCircle size={22} />
                      </button>
                    </div>
                  </div>

                  <div className="absolute top-0 right-0 flex flex-col gap-2 sm:mr-4">
                    <button
                      className="py-2 px-4 inline-flex items-center justify-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none rounded-l-full min-w-[100px]"
                      onClick={handlePrint}
                      disabled={isPrinting}
                    >
                      {isPrinting ? (
                        <div className="flex items-center justify-center">
                          <BeatLoader size={8} color="#ffffff" />
                        </div>
                      ) : (
                        "PDF"
                      )}
                    </button>

                    <button
                      className="py-2 px-4 items-center justify-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none rounded-l-full min-w-[100px]"
                      onClick={handleCSVDownload}
                    >
                      CSV
                    </button>
                  </div>
                </div>

                {/* Testata */}
                <div className="flex justify-center items-start gap-5 mt-[100px] md:mt-0 px-2">
                  <div className="py-[24px] border-white flex items-center flex-col md:flex-row justify-center gap-[4rem] flex-wrap">
                    <div className="text-center min-w-[150px]">
                      <p className="text-md mb-3">
                        Totale quantità ordinata residua
                      </p>
                      <h2 className="text-3xl text-blue-600">
                        {formatThousands(testata.quantita_ordinata) || 0}
                      </h2>
                    </div>
                    <div className="w-[1px] h-[150px] bg-slate-500 hidden md:block"></div>
                    <div className="text-center min-w-[150px]">
                      <p className="text-md mb-3">Valore ordinato residuo</p>
                      <h2 className="text-3xl text-blue-600">
                        {formatThousands(testata.importo_ordinato) || 0}
                      </h2>
                    </div>
                    <div className="w-[1px] h-[150px] bg-slate-500 hidden md:block"></div>
                    <div className="text-center min-w-[150px]">
                      <p className="text-md mb-3">Totale quantità assegnata</p>
                      <h2 className="text-3xl text-blue-600">
                        {formatThousands(testata.quantita_assegnata) || 0}
                      </h2>
                    </div>
                    <div className="w-[1px] h-[150px] bg-slate-500 hidden md:block"></div>
                    <div className="text-center min-w-[150px]">
                      <p className="text-md mb-3">Valore assegnato</p>
                      <h2 className="text-3xl text-blue-600">
                        {formatThousands(testata.importo_assegnato) || 0}
                      </h2>
                    </div>
                  </div>
                </div>

                <hr className="hidden lg:block border-neutral-300 dark:border-neutral-400" />

                {/* Container per i filtri */}
                <div className="flex flex-wrap justify-center items-center gap-2 my-8 px-2">
                  <div className="cu-m-wm2 flex flex-col items-start w-auto mb-1">
                    <h3 className="md:hidden cu-pd text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24 w-[100%]">
                      Ricerca
                    </h3>
                  </div>

                  {/* Filtro per Agente: visibile solo ai supervisor */}
                  {userRole === "supervisor" && (
                    <div className="cu-m-wm2 flex flex-col items-start w-auto">
                      <Select
                        options={agents.map((agent) => ({
                          value: agent.db2_user_id,
                          label: `${agent.first_name} ${agent.last_name}`,
                          // Include raw codes for easier access later
                          raw_codes: agent.db2_user_id,
                        }))}
                        value={selectedAgent}
                        onChange={handleAgentSelect}
                        isClearable={true}
                        placeholder="Seleziona Agente"
                        classNames={{
                          container: (state) => "w-full md:w-auto",
                          control: (state) =>
                            "cu-wi-se min-h-c border border-neutral-300 dark:border-neutral-400",
                          singleValue: (state) =>
                            "text-neutral-500 dark:text-neutral-400",
                          option: (state) =>
                            "hover:bg-blue-500 hover:text-white",
                        }}
                      />
                    </div>
                  )}

                  {/* Filtro per Codice Cliente */}
                  <div className="cu-m-wm2 flex flex-col items-start w-auto">
                    <Select
                      classNames={{
                        container: () => "w-full md:w-auto",
                        control: () =>
                          "cu-wi-se min-h-c border border-neutral-300 dark:border-neutral-400",
                        input: () => "!text-inherit",
                        singleValue: () =>
                          "text-neutral-900 dark:text-neutral-100",
                      }}
                      options={clienteFilters.map((cliente) => ({
                        value: cliente.codice_cliente,
                        label: `${cliente.ragione_sociale} (${cliente.codice_cliente})`,
                      }))}
                      value={
                        selectedCliente
                          ? {
                              value: selectedCliente,
                              label: searchCliente,
                            }
                          : null
                      }
                      onChange={handleClienteSelect}
                      isClearable={true}
                      placeholder="Cliente / Codice Cliente"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-center items-center gap-2 my-8 px-2">
                  <div className="cu-di-cont">
                    {/* Data Ordine Dal */}
                    <div className="cu-m-wm2 flex flex-col cu-m-wm cu-wi-se">
                      <label
                        htmlFor="fromOrderDate"
                        className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
                      >
                        Data Ordine Dal
                      </label>
                      <input
                        id="fromOrderDate"
                        type="date"
                        value={fromOrderDate || ""}
                        onChange={(e) => setFromOrderDate(e.target.value)}
                        className="border text-neutral-400 dark:border-neutral-500 rounded px-2 py-1 bg-transparent
                                  focus:ring-blue-500 focus:border-blue-500 w-full min-w-[180px]"
                      />
                    </div>

                    {/* Data Ordine Al */}
                    <div className="cu-m-wm2 flex flex-col cu-m-wm cu-wi-se">
                      <label
                        htmlFor="toOrderDate"
                        className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
                      >
                        Data Ordine Al
                      </label>
                      <input
                        id="toOrderDate"
                        type="date"
                        value={toOrderDate || ""}
                        onChange={(e) => setToOrderDate(e.target.value)}
                        className="border text-neutral-400 dark:border-neutral-500 rounded px-2 py-1 bg-transparent
                                  focus:ring-blue-500 focus:border-blue-500 w-full min-w-[180px] dark:calendar-white"
                      />
                    </div>

                    {/* Data Consegna Dal */}
                    <div className="cu-m-wm2 flex flex-col cu-m-wm cu-wi-se">
                      <label
                        htmlFor="fromDelivery"
                        className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
                      >
                        Data Consegna Dal
                      </label>
                      <input
                        id="fromDelivery"
                        type="date"
                        value={fromDelivery || ""}
                        onChange={(e) => setFromDelivery(e.target.value)}
                        className="border text-neutral-400 dark:border-neutral-500 rounded px-2 py-1 bg-transparent
                                  focus:ring-blue-500 focus:border-blue-500 w-full min-w-[180px] dark:calendar-white"
                      />
                    </div>

                    {/* Data Consegna Al */}
                    <div className="cu-m-wm2 flex flex-col cu-m-wm cu-wi-se">
                      <label
                        htmlFor="toDelivery"
                        className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
                      >
                        Data Consegna Al
                      </label>
                      <input
                        id="toDelivery"
                        type="date"
                        value={toDelivery || ""}
                        onChange={(e) => setToDelivery(e.target.value)}
                        className="border text-neutral-400 dark:border-neutral-500 rounded px-2 py-1 bg-transparent
                                  focus:ring-blue-500 focus:border-blue-500 w-full min-w-[180px] dark:calendar-white"
                      />
                    </div>
                  </div>

                  
                <div className="flex w-full flex-wrap flex-column justify-center items-center gap-2 my-2">
                  <div className="flex w-full sm:w-auto block py-2 px-3">
                    <button
                      type="button"
                      onClick={() => setIsFilterModalOpen(true)}
                      className="hidden lg:block min-h-c py-2 px-4 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      <span className="mr-2">Filtri Avanzati</span>
                      {getActiveFiltersCount() > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
                          {getActiveFiltersCount()}
                        </span>
                      )}
                    </button>

                    <div className="flex sm:flex-row flex-col w-full sm:w-auto gap-2">
                      <button
                        onClick={clearAllFilters}
                        className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700 w-full sm:w-auto"
                      >
                        Cancella filtri
                      </button>
                      <button
                        type="button"
                        onClick={handleSearch}
                        className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700 w-full sm:w-auto"
                      >
                        Cerca
                      </button>
                    </div>
                  </div>
                </div>

                  <div className="cu-fi-mob lg:hidden border border-neutral-300 cu-m-t">
                    <nav
                      className="grid grid-cols-2 divide-neutral-300"
                      aria-label="Tabs"
                      role="tablist"
                    >
                      <div
                        id="basic-tabs-item-1"
                        data-hs-tab="#basic-tabs-1"
                        className="active p-4 flex items-center gap-5 justify-between"
                        aria-controls="basic-tabs-1"
                        role="tab"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        <div className="flex gap-2">
                          <h4 className="text-blue-600 text-xl">Filtri</h4>
                          <span className="bg-blue-500 group-active:text-blue-500 group-active:bg-white text-md text-white px-1.5 ml-4">
                            {getActiveFiltersCount()}
                          </span>
                        </div>
                        <img
                          src={CloseIcon}
                          className="w-[24px] h-[24px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearFiltri(e);
                          }}
                        />
                      </div>

                      <div
                        id="basic-tabs-item-2"
                        className="p-4 flex justify-between items-center gap-5"
                        role="tab"
                        onClick={() => setShowOrderBy(!showOrderBy)}
                      >
                        <h4 className="text-blue-600 text-xl">Ordina Per</h4>
                        <img
                          src={CloseIcon}
                          className="w-[24px] h-[24px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowOrderBy(false);
                            clearOrderBy();
                          }}
                          alt="Close"
                        />
                      </div>
                    </nav>
                  </div>

                  {/* Container per Ordina Per, visibile su desktop di default */}
                </div>


                {/* Container per colonna filtri e tabella */}
                <div className="space-y-6 px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 mt-4">
                  <div
                    id="filter_menu"
                    className={`bg-white p-4 rounded-lg max-h-[calc(100vh-8rem)] cu-p-t overflow-y-auto w-full block lg:hidden ${
                      showFilters ? "" : "hidden"
                    }`}
                  >
                    <div className="flex justify-between items-center hidden lg:flex">
                      <div className="flex gap-2">
                        <h4 className="text-blue-600 text-xl">Filtri</h4>
                        <span className="bg-blue-500 group-active:text-blue-500 group-active:bg-white text-md text-white px-1.5 ml-4">
                          {getActiveFiltersCount()}
                        </span>
                      </div>
                      <img
                        src={CloseIcon}
                        className="w-[24px] h-[24px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearFiltri(e);
                        }}
                      />
                    </div>

                    {/* Filtro per Codice Articolo */}
                    <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                      <span>Codice Articolo</span>
                    </div>
                    <input
                      type="text"
                      value={codiceArticolo}
                      onChange={(e) => setCodiceArticolo(e.target.value.toUpperCase())}
                      placeholder="Es. ART001"
                      className="w-full border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-sm bg-transparent text-neutral-600 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    />

                    {/* Filtro per Ritiro Cliente */}
                    <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                      <span>Ritiro Cliente</span>
                    </div>
                    <div className="flex">
                      <label
                        htmlFor="ritiro_cliente_attivo"
                        className={`min-h-c flex justify-center p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          ritiroCliente === "Si" ? "bg-blue-600 text-white" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="ritiro_cliente_attivo"
                          name="ritiro_cliente"
                          className="hidden"
                          onChange={() => setRitiroCliente("Si")}
                          value="Si"
                          checked={ritiroCliente === "Si"}
                        />
                        Sì
                      </label>
                      <label
                        htmlFor="ritiro_cliente_no"
                        className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          ritiroCliente === "No" ? "bg-blue-600 text-white" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="ritiro_cliente_no"
                          name="ritiro_cliente"
                          className="hidden"
                          onChange={() => setRitiroCliente("No")}
                          value="No"
                          checked={ritiroCliente === "No"}
                        />
                        No
                      </label>
                      <label
                        htmlFor="ritiro_cliente_tutti"
                        className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          ritiroCliente === "Tutti"
                            ? "bg-blue-600 text-white"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="ritiro_cliente_tutti"
                          name="ritiro_cliente"
                          className="hidden"
                          onChange={() => setRitiroCliente("Tutti")}
                          value="Tutti"
                          checked={ritiroCliente === "Tutti"}
                        />
                        Tutti
                      </label>
                    </div>

                    {/* Filtro per Tipologia Consegna */}
                    <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                      <span>Tipologia Consegna</span>
                    </div>
                    <div className="flex">
                      <label
                        htmlFor="tipo_consegna_totale"
                        className={`min-h-c flex justify-center p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          tipoConsegna === "Totale"
                            ? "bg-blue-600 text-white"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="tipo_consegna_totale"
                          name="tipo_consegna"
                          className="hidden"
                          onChange={() => setTipoConsegna("Totale")}
                          value="Totale"
                          checked={tipoConsegna === "Totale"}
                        />
                        Totale
                      </label>
                      <label
                        htmlFor="tipo_consegna_parziale"
                        className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          tipoConsegna === "Parziale"
                            ? "bg-blue-600 text-white"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="tipo_consegna_parziale"
                          name="tipo_consegna"
                          className="hidden"
                          onChange={() => setTipoConsegna("Parziale")}
                          value="Parziale"
                          checked={tipoConsegna === "Parziale"}
                        />
                        Parziale
                      </label>
                      <label
                        htmlFor="tipo_consegna_tutti"
                        className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          tipoConsegna === "Tutti"
                            ? "bg-blue-600 text-white"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="tipo_consegna_tutti"
                          name="tipo_consegna"
                          className="hidden"
                          onChange={() => setTipoConsegna("Tutti")}
                          value="Tutti"
                          checked={tipoConsegna === "Tutti"}
                        />
                        Tutte
                      </label>
                    </div>

                    {/* Filtro per Quantità */}
                    <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                      <span>Quantità</span>
                    </div>
                    <div className="flex">
                      <label
                        htmlFor="quantita_assegnata_mobile"
                        className={`min-h-c flex justify-center p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          quantita === "Si" ? "bg-blue-600 text-white" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="quantita_assegnata_mobile"
                          name="quantita_mobile"
                          className="hidden"
                          onChange={() => setQuantita("Si")}
                          value="Si"
                          checked={quantita === "Si"}
                        />
                        Assegnata
                      </label>
                      <label
                        htmlFor="quantita_non_assegnata_mobile"
                        className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          quantita === "No" ? "bg-blue-600 text-white" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="quantita_non_assegnata_mobile"
                          name="quantita_mobile"
                          className="hidden"
                          onChange={() => setQuantita("No")}
                          value="No"
                          checked={quantita === "No"}
                        />
                        Non assegnata
                      </label>
                      <label
                        htmlFor="quantita_tutte_mobile"
                        className={`min-h-c flex justify-center p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white ${
                          quantita === "Tutti" ? "bg-blue-600 text-white" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          id="quantita_tutte_mobile"
                          name="quantita_mobile"
                          className="hidden"
                          onChange={() => setQuantita("Tutti")}
                          value="Tutti"
                          checked={quantita === "Tutti"}
                        />
                        Tutte
                      </label>
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={applyFilters}
                        className="cu-n-w cu-t-c py-2 px-4 w-full font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Filtra
                      </button>
                    </div>
                  </div>

                  {/* Ordina Per panel - mobile only */}
                  <div
                    className={`bg-white p-4 rounded-lg max-h-[calc(100vh-8rem)] cu-p-t overflow-y-auto w-full block lg:hidden ${
                      showOrderBy ? "" : "hidden"
                    }`}
                  >
                    {orderBy.length > 0 &&
                      orderBy.map((item) => {
                        const labelMap: Record<string, string> = {
                          ragione_sociale: "Ragione Sociale",
                          data_ordine: "Data Ordine",
                          data_consegna: "Data Consegna",
                        };
                        return (
                          <div className="grid grid-cols-4 border" key={item.column}>
                            <div className="col-span-4 px-3 py-1.5 flex items-center justify-between">
                              <h4>{labelMap[item.column] || item.column}</h4>
                              <div className="flex gap-2">
                                <span
                                  className={classNames({
                                    "material-symbols-outlined cursor-pointer text-xl": true,
                                    "text-neutral-300": item.mode !== "DESC",
                                    "text-blue-500": item.mode === "DESC",
                                  })}
                                  onClick={() => handleOrderByChange(item.column)}
                                >
                                  arrow_upward
                                </span>
                                <span
                                  className={classNames({
                                    "material-symbols-outlined cursor-pointer text-xl": true,
                                    "text-neutral-300": item.mode !== "ASC",
                                    "text-blue-500": item.mode === "ASC",
                                  })}
                                  onClick={() => handleOrderByChange(item.column)}
                                >
                                  arrow_downward
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    <div
                      className={classNames({
                        "dark:border-neutral-50 border-r border-b border border-dotted": true,
                        "border-t": orderBy.length === 0,
                      })}
                    >
                      <Select
                        onChange={(opt: any) => opt && handleOrderByChange(opt.value)}
                        value={""}
                        isTransparent
                        placeholder="+ Ordinamento"
                        options={visibleColumns.map((c) => {
                          const labelMap: Record<string, string> = {
                            ragione_sociale: "Ragione Sociale",
                            data_ordine: "Data Ordine",
                            data_consegna: "Data Consegna",
                          };
                          return { value: c, label: labelMap[c] || c };
                        })}
                      />
                    </div>

                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={applyFilters}
                        className="cu-n-w min-h-c p-2 px-4 items-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Applica
                      </button>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-neutral-800 rounded-lg overflow-hidden cu-sh-b">
                    <div
                      className="w-full table-container"
                      style={{
                        height: "500px",
                        overflowY: "auto",
                        overflowX: "auto",
                      }}
                    >
                      {loading ? (
                        <div className="flex justify-center items-center h-full">
                          <BeatLoader color="#2563eb" />
                        </div>
                      ) : (
                        <table className="w-full">
                          <thead>
                            <tr>
                              <th className="border px-4 py-2">N° Ordine</th>
                              <th className="border px-4 py-2">Dettaglio</th>
                              <th className="border px-4 py-2">
                                Ragione Sociale
                              </th>
                              <th className="border px-4 py-2">Rif. Cliente</th>
                              <th className="border px-4 py-2">Data Ordine</th>
                              <th className="border px-4 py-2">
                                Data Consegna Richiesta
                              </th>
                              <th className="border px-4 py-2">
                                Codice articolo
                              </th>
                              <th className="border px-4 py-2">
                                Descrizione Articolo
                              </th>
                              <th className="border px-4 py-2">Quantita Ordinata</th>
                              <th className="border px-4 py-2">
                                Importo Ordinato
                              </th>
                              <th className="border px-4 py-2">Quantita Residua</th>
                              <th className="border px-4 py-2">Importo Residuo</th>
                              <th className="border px-4 py-2">
                                Quantità Assegnata
                              </th>
                              <th className="border px-4 py-2">Importo Assegnato</th>
                              <th className="border px-4 py-2">Deposito</th>
                              <th className="border px-4 py-2">
                                Ritiro Cliente
                              </th>
                              <th className="border px-4 py-2">
                                Tipo Consegna
                              </th>
                            </tr>
                          </thead>
                          <tbody
                            className={`${
                              loading ? "opacity-0" : "opacity-100"
                            } transition-opacity duration-300`}
                          >
                            {data.length > 0 ? (
                              data.map((item: any, index: number) => (
                                <tr key={index}>
                                  <td className="border px-4 py-2">
                                    {item.NUMERO_ORDINE}
                                  </td>
                                  <td>
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/ordini/${item.NUMERO_ORDINE}-${item.ANNO_ORDINE}/detail`
                                        )
                                      }
                                      className="px-2 py-1 text-white rounded focus:outline-none focus:ring-2 focus:ring-opacity-50"
                                    >
                                      &#128269;
                                    </button>
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.RAGIONE_SOCIALE}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.RIFERIMENTO_CLIENTE}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {moment(item.DATA_ORDINE.toString()).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.DATA_CONSEGNA_RICHIESTA
                                      ? moment(
                                          item.DATA_CONSEGNA_RICHIESTA.toString()
                                        ).format("DD/MM/YYYY")
                                      : "-"}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.CODICE_ARTICOLO}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.DESCRIZIONE_ARTICOLO}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.QUANTITA}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {Number(item.PREZZO).toLocaleString(
                                      "it-IT",
                                      {
                                        style: "currency",
                                        currency: "EUR",
                                      }
                                    )}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.RESIDUO}
                                  </td>
                                <td className="border px-4 py-2">
                                    {Number(item.IMPORTO_RESIDUO).toLocaleString(
                                                 
                                      "it-IT",
                                      {
                                        style: "currency",
                                        currency: "EUR",
                                      }
                                    )}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.QUANTITA_ASSEGNATA}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {Number(item.IMPORTO).toLocaleString(
                                      "it-IT",
                                      {
                                        style: "currency",
                                        currency: "EUR",
                                      }
                                    )}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.DEPOSITO}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.RITIRO_CLIENTE}
                                  </td>
                                  <td className="border px-4 py-2">
                                    {item.TIPO_CONSEGNA}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={15}
                                  className="border px-4 py-2 text-center"
                                >
                                  Nessun dato disponibile
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {/* Paginazione */}
                  <Pagination />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
      {showInfoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-blue-600">🔎 Come leggere il Backorder</h3>
              <button
                onClick={() => setShowInfoModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4"
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Il backorder serve a capire in pochi secondi:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 mb-6 space-y-1">
              <li>cosa è ancora da consegnare</li>
              <li>per quale cliente</li>
              <li>quando è prevista la partenza della merce</li>
            </ul>

            <div className="mb-5">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">📅 ① Data di consegna richiesta</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">È il campo chiave.</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Le spedizioni vengono pianificate in base a questa data.<br />
                Se la data non è ancora arrivata:
              </p>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 mb-2">
                <li>→ la merce non entra nelle liste di uscita</li>
                <li>→ non viene preparata</li>
                <li>→ non viene spedita</li>
              </ul>
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Prima di sollecitare, verifica sempre questa data.
              </p>
            </div>

            <div className="mb-5">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">📦 ② Quantità residua</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Indica quanta merce manca ancora da consegnare al cliente.
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                È il calcolo matematico:<br />
                <span className="font-medium">Quantità ordinata – Quantità già consegnata</span>
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Se è maggiore di zero → l'ordine è ancora aperto.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">🏷 ③ Quantità assegnata</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Indica quanta parte della quantità residua è già allocata a sistema (AS400).
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Significa che:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1 mb-2">
                <li>lo stock è stato riservato</li>
                <li>la merce è disponibile per quell'ordine</li>
              </ul>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Se la quantità assegnata è inferiore alla residua → una parte è ancora in attesa di disponibilità.
              </p>
            </div>
          </div>
        </div>
      )}

      <BackorderFiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        theme={theme}
        fromDelivery={fromDelivery}
        toDelivery={toDelivery}
        setFromDelivery={setFromDelivery}
        setToDelivery={setToDelivery}
        ritiroCliente={ritiroCliente}
        setRitiroCliente={setRitiroCliente}
        tipoConsegna={tipoConsegna}
        setTipoConsegna={setTipoConsegna}
        quantita={quantita}
        setQuantita={setQuantita}
        codiceArticolo={codiceArticolo}
        setCodiceArticolo={setCodiceArticolo}
        applyFilters={applyFilters}
        clearFiltri={clearFiltri}
        orderBy={orderBy}
        handleOrderByChange={handleOrderByChange}
        clearOrderBy={clearOrderBy}
        visibleColumns={visibleColumns}
      />
    </div>
  );
}
